import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Container, Row, Col, Form, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { CircularProgress, Slider, createTheme, ThemeProvider, Button, SvgIcon, Tooltip } from '@material-ui/core';
import { MultiSelectInput, SelectInput } from '../../../Helpers/Inputs';
import IsMultiTerm from '../../Common/Helpers/IsMultiTerm';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import TobaccoUsage from '../../Common/Helpers/TobaccoUsage';
import MCTQuoteCard from '../../Common/Cards/MCTQuoteCard';
import NeedsAnalysisButton from '../../../Tools/NeedsAnalysis/NeedsAnalysisButton';
import { CollapsibleBasicInfoSummary } from '../../Common/SubComponents/BasicInfoSummary';
import NoQuoteAvailable from '../../Common/SubComponents/NoQuoteAvailable';
import MCTProductDetails from '../Helpers/MCTProductDetails';
import DigitalCoverageAmounts from '../../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts';
import GetProductTypeEnum from '../../../../types/productType';
import AffordableIcon from '../../../../icons/affordable.svg';
import FastIcon from '../../../../icons/fast.svg';
import MostOptionsIcon from '../../../../icons/most-options.svg';
import ContactHelpBlackIcon from '../../../../icons/contactHelpBlack.svg';
import NumberFormat from 'react-number-format';
import {
  SESSION_STORAGE_ACCOUNT_KEY,
  getSessionStorageItem,
  SESSION_STORAGE_ACTIVE_PURL,
  SESSION_STORAGE_TERM_COVERAGE_AMOUNT_KEY,
  SESSION_STORAGE_TERM_LENGTH_KEY,
  SESSION_STORAGE_TERM_PRE_RUN_QUOTE_KEY,
  SESSION_STORAGE_TERM_STRUCTURE,
  getBasicInfo,
  setBasicInfo,
  SESSION_STORAGE_COVR_FULLUW_KEY,
  SESSION_STORAGE_AMERICO_OBJECT_KEY,
  setSessionStorageItem,
} from '../../../../sessionStorage';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Age from '../../../Helpers/Dates/Age';
import '../../Quoter.scss';
import TobbacoConverter from '../Helpers/TobbacoConverter';
import GetAmericoHealthClass from '../Helpers/GetAmericoHealthClass';
import { QuoteProductID } from '../../Common/QuoteProductID';
import BasicInfoSummaryModel from '../../Common/SubComponents/BasicInfoSummaryModel';
import AboutMe from './AboutMe';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const quoterTheme = {
  typography: {
    fontFamily: "museo-sans",
    body1: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  overrides: {
    MuiFormControl: {
      root: {
        "&.coverage-amount": {
          display: "flex",
          alignItems: "center",
        },
        "&.coverage-amount .coverage-input": {
          display: "flex",
          width: 188,
          height: 47,
          alignItems: "center",
          borderRadius: 6,

        },
        "&.coverage-amount .coverage-input::before": {
          content: "none",
        },
        "&.coverage-amount .coverage-input::after": {
          content: "none",
        },
        "&.coverage-amount .coverage-input input": {
          padding: 0,
        },
      },
    },

    MuiInputBase: {
      input: {
        fontSize: 16,
      },
      root: {
        height: 56,
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
      underline: {
        "&::before": {
          borderBottom: "solid 2px #b2b2b2",
        },
        "&:hover::before": {
          borderBottomWidth: "2px",
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 16,
        color: "rgba(0,0,0,0.6)",
      },
    },
    MuiFormHelperText: {
      root: {
        marginTop: 0,
        paddingTop: 0,
      },
    },
    MuiInputLabel: {
      shrink: {
        color: "rgba(0,0,0,0.3) !important",
      },
    },
    MuiSelect: {
      fontSize: 16,
      icon: {
        color: "rgba(0,0,0,1)",
      },
      iconFilled: {
        right: 16,
      },
    },
    MuiSlider: {
      root: {
        height: 4,
        margin: "8px 0",
        padding: 0,
      },
      rail: {
        height: 4,
        borderRadius: 2,
        backgroundColor: " #ccc",
        opacity: 1,
      },
      track: {
        height: 4,
        borderRadius: 2,
      },
      mark: {
        display: "none",
      },
      thumb: {
        marginTop: -4,
      },
      markLabel: {
        top: 9,
        fontSize: 12,
        color: "#333",
        "&[style*='0%;']": {
          "left": "18px !important",
        },
        "&[style*='100%;']": {
          "right": "-15px !important",
          "left": "auto !important",
        },
      },
    },
    MuiSvgIcon: {
      root: {
        width: 24,
        height: 24,
        marginBottom: 1,
      },
    },
    MuiPaper: {
      outlined: {
        border: "none",
        boxShadow: "0 2px 3px rgba(0, 0, 0, 0.161)",
      },
    },
  },
};

export default function Quoter(props) {
  const termPreRunQuoteObj = getSessionStorageItem(SESSION_STORAGE_TERM_PRE_RUN_QUOTE_KEY);
  const covgAmt = termPreRunQuoteObj && termPreRunQuoteObj.pages.predictiveBasicInfo.coverageAmount;
  const preQuoteCovgAmt = props.formData.preQuote && props.formData.preQuote.coverageAmountQuote;
  const preQuoteTerm = props.formData.preQuote && props.formData.preQuote.termQuote;

  const basicInfo = getBasicInfo();

  const [loading, setLoading] = useState(false);
  const [quoteRan, setQuoteRan] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [coverageAmount, setCoverageAmount] = useState(basicInfo.faceAmount || parseInt(props.formState.coverageAmount, 10) || preQuoteCovgAmt || sessionStorage.getItem(SESSION_STORAGE_TERM_COVERAGE_AMOUNT_KEY) || covgAmt);
  const [term, setTerm] = useState(props.formState.term || basicInfo.termLength || preQuoteTerm || sessionStorage.getItem(SESSION_STORAGE_TERM_LENGTH_KEY) || (termPreRunQuoteObj && termPreRunQuoteObj.pages.predictiveBasicInfo.term) || getDefaultTermLength(basicInfo.tobacco, basicInfo.birthdate));
  const [healthClassJSON, setHealthClassJSON] = useState(null);
  const [multiTermFilter, setMultiTermFilter] = useState(false);
  const [multiTermResult, setMultiTermResult] = useState(props.multiTermResult);
  
  const [basicInfoModalShow, setBasicInfoModalShow] = useState(false);
  const getActivePurlData = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL);
  const isActivePurl = getActivePurlData ? getActivePurlData.active : false;
  const [isPurlVerification, setIsPurlVerification] = useState(isActivePurl);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const accountProductTypeSettings = accountObj.accountProducts.find(product => product.productLandingUrl === '/landing/term');
  const accountLandingOverride = accountObj.accountOverrides && accountProductTypeSettings ? 
    accountObj.accountOverrides.find(override => override.accountProductTypeId === accountProductTypeSettings.productTypeId) : null;

  const quoteResultTermStructure = getSessionStorageItem(SESSION_STORAGE_TERM_STRUCTURE)
  const termAccountProduct = accountObj && accountObj.accountProducts.find(e => e.productTypeId === GetProductTypeEnum().FullUW);
  const bestowHandoffAccount = termAccountProduct && termAccountProduct.bestowHandoffAccount;

  const isHavenSI = accountObj.accountProducts.some((item) => {
    return item.productTypeId === GetProductTypeEnum().HavenSI;
  });

  const isDecreasingTermActivated = (props.formState.termStructure && props.formState.termStructure.length > 0 && props.formState.termStructure.includes('Decreasing')) || false;

  useEffect(() => {
    window.scroll(0, 0);
    const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_COVR_FULLUW_KEY);
    const sessionStorageBasicFormState = getBasicInfo();
    if (!sessionStorageFormState && Object.values(sessionStorageBasicFormState).every(x => x === null || x === '')) {
      props.history.push('/quote/term-life-insurance/multi-carrier-term/about-me');
    }

    props.formState.termStructure = ['Level', 'Decreasing'];
  
  }, []);

  useEffect(() => {
    quoteResultTermStructure ? isMultiTermFilter(quoteResultTermStructure.multiTerm) : isMultiTermFilter(false);
    if (quoteResultTermStructure && quoteResultTermStructure.multiTerm) {
      setMultiTermResult(quoteResultTermStructure.multiTerm)
    }
  }, [props.multiTermResult]);

  useEffect(() => {
    setSessionStorageItem(SESSION_STORAGE_TERM_STRUCTURE, {multiTerm: multiTermResult});
    isMultiTermFilter(multiTermResult)
  }, [multiTermResult])

  const hideAllDefault = accountObj.accountProducts.find((item) => item.productTypeId === GetProductTypeEnum().FullUW).hideAllProductsDefault;
  const tobacco = TobaccoUsage(basicInfo.tobacco);

  const isMultiTermFilter = (multiTermResult) => {
    if (!multiTermResult) {
      setMultiTermFilter(false);
    } else {
      setMultiTermFilter(true);
    }
  };

  const runQuote = async (getIsSubscribed) => {
    setQuoteRan(true);

    const basicInfo = getBasicInfo();

    const getMCTQuotes = (json) =>{
      const feet = basicInfo.height === "" ? "0" : basicInfo.height.split('ft')[0].trim();
      const inches = basicInfo.height === "" ? "0" : basicInfo.height.split('ft')[1].split('in')[0].trim();
      const weight = basicInfo.weight === "" ? "0" : basicInfo.weight.split(' ')[0];

      const queryParams = {
        birthdate: basicInfo.birthdate || basicInfo.fakeBirthdate,
        state: basicInfo.state,
        gender: basicInfo.gender,
        faceAmount: coverageAmount,
        tobacco: tobacco,
        healthClass: json.defaultHealthClass,
        term: term,
        heightFt: feet,
        heightIn: inches,
        weight: weight,
        oneClickAccountId: accountObj.account.oneClickAccountId,
        replacePolicy: basicInfo.replacePolicy || false,
        noKnockouts: json.isQualifiedForDigital,
        sessionId: accountObj.sessionId,
        isLevelTerm: (props.formState.termStructure && props.formState.termStructure.length > 0 && props.formState.termStructure.includes('Level')) || false,
        isDecreasingTerm: (props.formState.termStructure && props.formState.termStructure.length > 0 && props.formState.termStructure.includes('Decreasing')) || false,
      };

      if (bestowHandoffAccount) {
        queryParams.carrierData = [
          {
            carrier: 'bestow',
            product: 'bestow_digital',
            items: {
              "short_url_id": bestowHandoffAccount,
            },
          },
        ];
      }

      fetch(`${covrServicesUrl}/api/MCT/GetMCTQuote`, {
            method: 'POST',
            headers: {
               SessionId: getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(queryParams),
         })
         .then((res) => res.json())
         .then((data) => {
            if (getIsSubscribed()) {
               if (data.quoteResults.length >= 4) {
                  const bestPrice = data.quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                  bestPrice.isBestPrice = true;

                  const fastest = data.quoteResults.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                  fastest.isFastest = true;

                  const mostOptions = data.quoteResults.sort((a, b) => b.optionsScore - a.optionsScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                  mostOptions.isMostOptions = true;
               }

               if (!quoteResultTermStructure.multiTerm) {
                setMultiTermResult(IsMultiTerm(data));
               }

               let utmQuoterCarrierId = null;

               if (accountLandingOverride) {
                  const quoterCarrierId = accountLandingOverride.quoterCarrierId;

                  utmQuoterCarrierId = quoterCarrierId
               }

               data.quoteResults.forEach(qr => {
                 qr.quoteLogId = data.quoteLogId;
                 qr.showTermStruct = multiTermResult;
                 qr.isFeaturedCarrier = utmQuoterCarrierId === (qr.carrierID ? qr.carrierID : null);
               });

               setQuoteResults(data.quoteResults);

               setLoading(false);
            }
         })
         .catch((error) => {
            if (getIsSubscribed()) {
               ErrorHandler(error, setQuoteResults);
               setLoading(false);
            }
         });
      }

     if (props.formData.aboutMe.height !== "" && props.formData.aboutMe.weight !== "") {
       if(healthClassJSON){
          getMCTQuotes(healthClassJSON);
       }
       else {
        fetch(`${process.env.REACT_APP_COVR_SERVICES_URL}/api/VSS/GetCustomHealthClass`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(props.formData),
        })
          .then(res => res.json())
          .then(json => {
            props.onFieldChange('healthClass', json.defaultHealthClass);
            setHealthClassJSON(json);
            getMCTQuotes(json);
          })
       }
     } else if (isActivePurl) {
      const healthClassJSON = {
        "defaultHealthClass" : "PP",
        "isQualifiedForDigital" : true,
      }
      setHealthClassJSON(healthClassJSON);
      getMCTQuotes(healthClassJSON);
     }
  };

  let debounce = null;
  const setQuoteTimer = () => {
    setLoading(true);

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };
  }
  
  useEffect(() => {
    if ((props.formState.coverageAmount == null || props.formState.healthClass == null || props.formState.term == null) && !isActivePurl) {
      return;
    }
    return setQuoteTimer();
  }, [coverageAmount, term, props.formState.termStructure]);

  const getDefaultTermLength = (tobacco, dateOfBirth) => {
    const age = dateOfBirth ? Age(dateOfBirth) : null;
    const useTobacco = tobacco === 'true' ? true : false;
    if (age < 18) {
      return '';
    } else if (age > 17 && age < 66) {
      return '20';
    } else if (age > 65 && age < 71 && !useTobacco) {
      return '20';
    } else if (age > 65 && age < 71 && useTobacco) {
      return '15';
    } else if (age > 70 && age < 76 && !useTobacco) {
      return '15';
    } else if (age > 70 && age < 76 && useTobacco) {
      return '10';
    } else if (age > 75 && age < 81 && !useTobacco) {
      return '10';
    } else {
      return '';
    }
  };

  const handleQuoteSelection = (quoteInfo, callbacks) => {

    const { onNext, onNextBestow, onNextHaven, onNextLga } = callbacks;
    const { selectedQuote } = quoteInfo;

    switch (selectedQuote.productID) {

      case QuoteProductID.HAVEN_TERM_PRODUCT_ID:
      case QuoteProductID.HAVEN_SI_PRODUCT_ID:
        onNextHaven();
        break;
      case QuoteProductID.BESTOW_PRODUCT_ID:
        onNextBestow();
        break;
      case QuoteProductID.LGA_PRODUCT_ID:
        onNextLga();
        break;

      default:
        onNext();
        break;
    }
  }

  const getYearsOfCoverageOption = () => {
    const options = [
      { value: '10', label: '10-Year Term' },
      { value: '15', label: '15-Year Term' },
      { value: '20', label: '20-Year Term' },
      { value: '25', label: '25-Year Term' },
      { value: '30', label: '30-Year Term' },
      { value: '35', label: '35-Year Term' },
      { value: '40', label: '40-Year Term' },
    ]
    if(isDecreasingTermActivated){
      options.unshift({value: '5', label: '5-Year Term'}, {value: '7', label: '7-Year Term'})
    }
    return options;
  }

  const quoteOnClick = (quote) => {
    const basicInfo = getBasicInfo();
    if (isActivePurl && !basicInfo.birthdate) {
      setIsPurlVerification(true);
      setBasicInfoModalShow(true);
      setSelectedQuote(quote);
    } else {
      const form = { ...props.fullFormState };
      form.pages.quoter.coverageAmount = coverageAmount;
      form.pages.quoter.term = term;
      props.onMultiFieldChange(form);

      basicInfo.faceAmount = coverageAmount;
      basicInfo.termLength = term;
      setBasicInfo(basicInfo);

      const quoteInfo = {
        coverageAmount: coverageAmount,
        termLength: term,
        healthClass: props.formState.healthClass,
        isHavenSI: isHavenSI,
        selectedQuote: quote,
      };

      const callbacks = {
        onNext: props.onNext,
        onNextBestow: props.onNextBestow,
        onNextHaven: props.onNextHaven,
        onNextLga: props.onNextLga,
      };

      handleQuoteSelection(quoteInfo, callbacks);
    }
  };

  const handleBasicInfoSubmit = (open, purlMismatch) => {
    setBasicInfoModalShow(open);
    const basicInfo = getBasicInfo();
    setBasicInfo(basicInfo);
    props.onMultiFieldChange({
      ...props.fullFormState, 
      pages: {
        ...props.fullFormState.pages, 
        aboutMe: AboutMe.DefaultFormState(false, basicInfo),
      },
    });
    if (isPurlVerification && !purlMismatch) {
      quoteOnClick(selectedQuote);
    } else {
      setQuoteTimer();
    }
  }

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...quoterTheme,
    })}>
      <Container className="quoter-wrapper">
        <Row className="quoter">
          <Col lg={4} className="mb-3">
            <Form onSubmit={e => e.preventDefault()}>
              <h2>Your Coverage Details</h2>

              <Form.Group className="mb-0 coverage-amount-container">
                <NumberFormat
                  className="quote-amount-dollars input-coverage-amount"
                  value={coverageAmount}
                  displayType={'text'}
                  thousandSeparator={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Form.Group>

              <Form.Group className="coverage-slider">
                <Slider
                  min={100000}
                  max={3000000}
                  step={null}
                  value={coverageAmount}
                  onChange={(event, value) => setCoverageAmount(value)}
                  marks={DigitalCoverageAmounts(false)}
                  role="slider"
                  aria-valuemin={100000}
                  aria-valuenow={coverageAmount}
                  aria-valuemax={3000000}
                  aria-labelledby="coverageAmount"
                />
              </Form.Group>

              <Form.Group className="coverage-terms">
                <SelectInput
                  name="term"
                  id="term"
                  value={String(term)}
                  onChange={(e) => setTerm(e.target.value)}
                  label="Years of Coverage"
                  hideEmptyOption
                  selectOptions={getYearsOfCoverageOption()}
                />
              </Form.Group>

              {multiTermFilter && (
                <Form.Group className="coverage-terms">
                  <MultiSelectInput
                    name="termStructure"
                    id="termStructure"
                    value={props.formState.termStructure ? props.formState.termStructure : []}
                    onChange={(e) => props.onFieldChange("termStructure", e.target.value)}
                    label="Term Structure"
                    selectOptions={[
                      { value: 'Level', label: 'Level Term' },
                      { value: 'Decreasing', label: 'Decreasing Term' },
                    ]}
                  />
                </Form.Group>
              )}

              <CollapsibleBasicInfoSummary
                gender={props.basicInfo.gender}
                state={props.basicInfo.state}
                birthdate={props.basicInfo.birthdate || props.basicInfo.fakeBirthdate}
                tobacco={TobaccoUsage(props.basicInfo.tobacco)}
                height={props.basicInfo.height}
                weight={props.basicInfo.weight}
                showEditLink={true}
                editLinkOnClick={() => props.history.push('/quote/term-life-insurance/multi-carrier-term/about-me')}
              />
              <div id="sr-message" role="alert" aria-live="assertive" className="sr-only">
                <h2>{loading ? "loading.." : "product list updated"}</h2>
              </div>
            </Form>

            <h2 className="mt-18 mb-12">Insurance Tools</h2>

            <NeedsAnalysisButton
              type="quote-screen"
              minCoverageAmount={25000}
              maxCoverageAmount={1500000}
              setCoverageAmount={(face) => {
                setCoverageAmount(face);
                props.onFieldChange('coverageAmount', face);
              }}
              title="How much I need"
            />
          </Col>

          <Col lg={8} className="result-container">
            <QuoteResult
              formState={props.formState}
              result={quoteResults}
              quoteRan={quoteRan}
              loading={loading}
              onNext={props.onNext}
              quoteOnClick={quoteOnClick}
              hideAllDefault={hideAllDefault}
              coverageAmount={coverageAmount}
              term={term}
              fullFormState={props.fullFormState}
              onMultiFieldChange={props.onMultiFieldChange}
              history={props.history}
              healthClass={props.formState.healthClass}
              showTermStruct={multiTermResult}
            />
          </Col>
        </Row>
      
        {basicInfoModalShow && 
          <BasicInfoSummaryModel 
            openEditPopUp={basicInfoModalShow}
            closeModal={() => setBasicInfoModalShow(false)} 
            setOpenEditPopUp={handleBasicInfoSubmit}
            isPurlVerification={isPurlVerification}
            product='term2'
            selectedQuote={selectedQuote}
            onFieldChange={props.onFieldChange}
            formState={basicInfo} />}
      </Container>
    </ThemeProvider>
  );
}

function QuoteResult(props) {
  const useStyles = makeStyles((theme) => ({
    textColor: {
      color: theme.palette.primary.main,
    },
    backgroundGradient: {
      "&:hover": {
        background: ` linear-gradient(0deg, ${theme.palette.primary.main}00 0%, ${theme.palette.primary.main}15 100%)`,
      },
      "&.active": {
        background: `linear-gradient(0deg, ${theme.palette.primary.main}15 0%,  ${theme.palette.primary.main}00 100%)`,
      },
    }, backgroundColor: {
      backgroundColor: theme.palette.primary.main,
    },
  }));

  const classes = useStyles();

  const [expanded, setExpanded] = useState(!props.hideAllDefault);
  const [quoteCards, setQuoteCards] = useState([]);
  const [sortBy, setSortBy] = useState('price');
  const [indicatorPosition, setIndicatorPosition] = useState(0);
  const [quoterTooltip, setQuoterTooltip] = useState(false);
  const [hasBestPrice, setHasBestPrice] = useState([]);
  const [hasNoExamRequired, setHasNoExamRequired] = useState([]);
  const [hasMostOptions, setHasMostOptions] = useState([]);
  const handleTooltipClose = () => {
    setQuoterTooltip(false);
  };
  const handleTooltipOpen = () => {
    setQuoterTooltip(true);
  };
  const [americoModalShow, setAmericoModalShow] = useState(false);
  const handleAmericoModalClose = () => setAmericoModalShow(false);
  const handleAmericoModalShow = () => setAmericoModalShow(true);

  const quoteSortTrackRef = useRef();
  const priceRef = useRef();
  const speedRef = useRef();
  const optionsRef = useRef();

  const makeQuoteCard = (quote, index, sortType, props) => {
    return {
      speedScore: quote.speedScore,
      priceScore: quote.monthlyRate,
      optionsScore: quote.optionsScore,
      isBestPrice: quote.isBestPrice,
      isFastest: quote.isFastest,
      isMostOptions: quote.isMostOptions,
      isFeatured: quote.isFeaturedCarrier,
      totalScore: quote.totalUnweightedScore,
      component: (
        <MCTQuoteCard
          carrierName={quote.carrierName}
          productName={quote.productName}
          val={quote.termLength}
          annualRate={quote.annualRate}
          monthlyRate={quote.monthlyRate}
          logoUrl={quote.logoUrl}
          rating={quote.rating}
          requiresMedicalExam={quote.requiresMedicalExam}
          underwritingTimeTypical={quote.underwritingTimeTypical}
          quote={quote}
          key={index}
          isDecreasingTerm={quote.isDecreasingTerm}
          isFeaturedCarrier={quote.isFeaturedCarrier}
          showTermStruct={props.showTermStruct}
          onNext={() => handleAmericoModalShow()}
          onClick={() => {
            const form = { ...props.fullFormState };
            form.pages.quoter.carrierName = quote.carrierName;
            form.pages.quoter.carrierLogoUrl = quote.logoUrl;
            form.pages.quoter.premium = quote.monthlyRate;
            form.pages.quoter.product = quote.product;
            form.pages.quoter.cashValue = quote.cashValue !== null ? quote.cashValue : null;
            form.pages.quoter.logoUrl = quote.logoUrl;
            form.pages.quoter.selectedQuote = quote;
            props.onMultiFieldChange(form);
            if (quote.carrierName !== "Americo") {
              props.quoteOnClick(quote);
            } else {
              handleAmericoModalShow();
            }
          }}
          productDetails={
            <MCTProductDetails
              carrierName={quote.carrierName}
              productDescription={quote.productDescription}
              hasPermConversion={quote.hasConvertibility}
              productInformation={quote.productInformation}
            />
          }
          sortBy={sortType}
          isBestPrice={quote.isBestPrice}
          isFastest={quote.isFastest}
          isMostOptions={quote.isMostOptions}
          ratingAMBest={quote.ratingAMBest}
          ratingSP={quote.ratingSP}
          ratingMoodys={quote.ratingMoodys}
          ratingFitch={quote.ratingFitch}
          ratingComdex={quote.ratingComdex}
          availableRiders={quote.availableRiders}
          convertibility={quote.convertibility}
          hasPermConversion={quote.hasConvertibility}
          disclosure={quote.disclosureDetail}
        />
      ),
    };
  };

  const handleAmericoQuoteOnNext = () => {
    const form = { ...props.fullFormState };
    const smoker = form.pages.aboutMe.tobacco;
    const tobacco = TobbacoConverter(smoker);
    const healthClassValue = GetAmericoHealthClass(props.healthClass);
    const AMERICO_SESSION_STORAGE_KEY = "AMERICO_QUOTE";
    const mctData = {
      tobacco : tobacco,
      coverageAmount : props.coverageAmount,
      healthClass : healthClassValue,
      premium : form.pages.quoter.premium,
      term : props.term,
      cashValue: form.pages.quoter.cashValue,
      logoUrl : form.pages.quoter.logoUrl,
      isFromAmerico : true,
    }
    setSessionStorageItem(SESSION_STORAGE_AMERICO_OBJECT_KEY, mctData)
    sessionStorage.removeItem(AMERICO_SESSION_STORAGE_KEY);
    props.history.push('/loan-protection/getting-started');
  }

  const sortCards = () => {
    if (props.result && props.result.length > 0) {
      const newCards = props.result.map((r, index) => makeQuoteCard(r, index, sortBy, props));
      newCards.sort((a, b) => sorter(a, b, sortBy));
      setQuoteCards([...newCards]);
    }
  };

  const sorter = (a, b, sortByField) => {
    if(a.isFeatured) {return -1;}
    if(b.isFeatured) {return 1;}
    switch (sortByField) {
      case 'price':
        return a.priceScore - b.priceScore || b.totalScore - a.totalScore;
      case 'speed':
        return b.speedScore - a.speedScore || a.priceScore - b.priceScore || b.totalScore - a.totalScore;
      case 'options':
      default:
        return b.optionsScore - a.optionsScore || a.priceScore - b.priceScore || b.totalScore - a.totalScore;
    }
  };

  const getSelectedPosition = (sortSelected) => {
    setIndicatorPosition((sortSelected.current.getBoundingClientRect().x - quoteSortTrackRef.current.getBoundingClientRect().x));
  }

  const resetIndicatorPosition = () => {
    if (priceRef && priceRef && priceRef.current && priceRef.current.classList.contains("active")) {
      getSelectedPosition(priceRef);
    }
    if (speedRef && speedRef.current && speedRef.current.classList.contains("active")) {
      getSelectedPosition(speedRef);
    }
    if (optionsRef && optionsRef.current && optionsRef.current.classList.contains("active")) {
      getSelectedPosition(optionsRef);
    }
  }

  useEffect(() => {
    if (!props.result || props.result.length < 1) {
      return;
    }

    if (props.result.length >= 4) {
      setHasBestPrice(props.result.filter(c => c.isBestPrice === true));
      setHasNoExamRequired(props.result.filter(e => e.isFastest === true));
      setHasMostOptions(props.result.filter(o => o.isMostOptions === true));
    } else {
      setHasBestPrice(null);
      setHasNoExamRequired(null);
      setHasMostOptions(null);
    }

    window.addEventListener("resize", resetIndicatorPosition);

    sortCards();
    return () => window.removeEventListener("resize", resetIndicatorPosition);
  }, [props.result, sortBy]);

  if (props.loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  if (!props.result.length) {
    if (props.quoteRan) {
      return <NoQuoteAvailable subText="We’d like to talk to you about your coverage requirement to make sure the product solutions will meet all of your needs. Contact one of our licensed experts to discuss your options." />;
    } else {
      return <span />;
    }
  }

  const PriceHelpIcon = forwardRef((props, ref) => {
    return (
      <div {...props} ref={ref}>
        <ContactHelpBlackIcon />
      </div>
    )
  });

  const SpeedHelpIcon = forwardRef((props, ref) => {
    return (
      <div {...props} ref={ref}>
        <ContactHelpBlackIcon />
      </div>
    )
  });

  const OptionsHelpIcon = forwardRef((props, ref) => {
    return (
      <div {...props} ref={ref}>
        <ContactHelpBlackIcon />
      </div>
    )
  });

  return (
    <div className="quote-result-section">
      <div className='coverage-and-term-summary'>
        <div className="quote-sort-container">
          <div className="quote-sort-header">
            <h2><NumberFormat value={props.coverageAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />&nbsp; - {props.term} Year Term Life Insurance</h2>
          </div>
          {hasBestPrice &&
            <div className="quote-sort-selection">
              <div ref={quoteSortTrackRef} className="quote-sort-track">
                <ul>
                  <li ref={priceRef} className={`sort-option ${sortBy === "price" ? "active" : ""} ${classes.backgroundGradient}`} onClick={() => { setSortBy('price'); getSelectedPosition(priceRef) }}>
                    <div className="icon-container">
                      <SvgIcon component={AffordableIcon} color="primary" />
                    </div>
                    <div className="content-container">
                      <p className={`title ${classes.textColor}`}>Lowest Price</p>
                      <p className="cost"><NumberFormat value={hasBestPrice[0].monthlyRate} displayType={'text'} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} prefix={'$'} />/mo</p>
                    </div>
                    <div className="help-container">
                      <LightTooltip title="Companies and products offering the best pricing based on your inputs.">
                        <PriceHelpIcon className="contact-help" />
                      </LightTooltip>
                    </div>
                  </li>
                  <li ref={speedRef} className={`sort-option ${sortBy === "speed" ? "active" : ""} ${classes.backgroundGradient}`} onClick={() => { setSortBy('speed'); getSelectedPosition(speedRef) }}>
                    <div className="icon-container">
                      <SvgIcon component={FastIcon} color="primary" />
                    </div>
                    <div className="content-container">
                      <p className={`title ${classes.textColor}`}>Fastest Process</p>
                      <p className="cost"><NumberFormat value={hasNoExamRequired[0].monthlyRate} displayType={'text'} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} prefix={'$'} />/mo</p>
                    </div>
                    <div className="help-container">
                      <LightTooltip title="Companies whose digital underwriting will get you coverage quickly and possibly without a medical exam.">
                        <SpeedHelpIcon className="contact-help" />
                      </LightTooltip>
                    </div>
                  </li>
                  <li ref={optionsRef} className={`sort-option ${sortBy === "options" ? "active" : ""} ${classes.backgroundGradient}`} onClick={() => { setSortBy('options'); getSelectedPosition(optionsRef) }}>
                    <div className="icon-container">
                      <SvgIcon component={MostOptionsIcon} color="primary" />
                    </div>
                    <div className="content-container">
                      <p className={`title ${classes.textColor}`}>Most Options</p>
                      <p className="cost"><NumberFormat value={hasMostOptions[0].monthlyRate} displayType={'text'} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} prefix={'$'} />/mo</p>
                    </div>
                    <div className="help-container">
                      <LightTooltip title="Products offering most flexibility such as conversion to more permanent coverage.">
                        <OptionsHelpIcon className="contact-help" />
                      </LightTooltip>
                    </div>
                  </li>
                </ul>
                <span style={{ left: `${indicatorPosition }px` }} className={`indicator ${classes.backgroundColor}`} />
              </div>
            </div>
          }
        </div>
      </div>
      <Row>
        <Col>
          <ul className="list-unstyled">
            {quoteCards.map((q, key) => <li key={key} className={key}>{q.component}</li>)}
          </ul>
        </Col>
      </Row>

      <Modal show={americoModalShow} onHide={handleAmericoModalClose} backdrop="static" centered>
        <Modal.Body className="model-body d-flex align-items-center justify-content-center p-3 flex-column ">
          <p className='model-header'>LoanMatch Protector is a decreasing term product</p>
          <p className='model-content'>Your insurance coverage will decrease over time to match the balance of a loan or other decreasing financial obligation.</p>
          <Row>
            <div className='pt-2'>
              <Button variant='contained' size="medium" color="primary" onClick={handleAmericoQuoteOnNext}>Continue</Button>
            </div>
          </Row>
          <Row className='mt-2'>
            <div>
              <Button size="medium" color="primary" onClick={handleAmericoModalClose}>Back</Button>
            </div>
          </Row>
        </Modal.Body>
      </Modal>
    </div>
  );
}

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  multiTermResult: PropTypes.bool.isRequired,
};

Quoter.DefaultFormState = (prefill = false, clientInfo = null) => {
  return {
    coverageAmount: (clientInfo && clientInfo.faceAmount) || 0,
    healthClass: (clientInfo && clientInfo.healthClass) || 'P',
    term: (clientInfo && clientInfo.termLength) || 0,
    termStructure: ['Level', 'Decreasing'],
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/MCT/steps/Quoter.js